import { useState, useEffect } from 'react';
import QuoteSimulator from './QuoteSimulator';
import type { SimulatorQuoteData } from '../page';

interface HeroProps {
  onSimulatorQuote: (data: SimulatorQuoteData) => void;
}

const HOUSE_ITEMS = [
  { name: '전기 및 조명', desc: '조명 설비 점검·교체 및 전기 안전 관리', img: 'https://public.readdy.ai/ai/img_res/b7775db8b625cfb65bb4d4c376b270a9.jpg' },
  { name: '수처리·위생', desc: '배관 점검·보수 및 정수 시스템 유지', img: 'https://readdy.ai/api/search-image?query=professional%20plumber%20water%20pipe%20inspection%20Korean%20house%20bathroom%20plumbing%20clean%20bright%20environment&width=360&height=220&seq=h-svc-02&orientation=landscape' },
  { name: '냉·난방 환기', desc: '에어컨·보일러 점검 및 환기 시스템 관리', img: 'https://readdy.ai/api/search-image?query=HVAC%20technician%20boiler%20inspection%20heating%20cooling%20system%20Korean%20house%20professional%20maintenance%20clean&width=360&height=220&seq=h-svc-03&orientation=landscape' },
  { name: '내·외부 마감재', desc: '창호·벽면 보수 및 도장 유지관리', img: 'https://readdy.ai/api/search-image?query=professional%20exterior%20facade%20window%20repair%20Korean%20house%20fresh%20white%20paint%20bright%20daylight%20clean%20finish&width=360&height=220&seq=h-svc-04&orientation=landscape' },
  { name: '조경·외부시설', desc: '정원 조경 관리 및 외부 시설물 유지보수', img: 'https://readdy.ai/api/search-image?query=professional%20landscaper%20garden%20maintenance%20Korean%20countryside%20trimmed%20hedges%20lawn%20stone%20pathway%20bright&width=360&height=220&seq=h-svc-05&orientation=landscape' },
  { name: '소방·보안 설비', desc: '소화기·경보기 점검 및 CCTV 유지', img: 'https://readdy.ai/api/search-image?query=fire%20safety%20CCTV%20smoke%20detector%20inspection%20Korean%20house%20professional%20maintenance%20bright%20clean&width=360&height=220&seq=h-svc-06&orientation=landscape' },
  { name: '조경 리모델링', desc: '정원 새단장 리모델링 서비스', img: 'https://readdy.ai/api/search-image?query=garden%20landscape%20remodeling%20Korean%20countryside%20house%20new%20stone%20path%20flowers%20clean%20lawn%20bright%20sunny&width=360&height=220&seq=h-svc-07&orientation=landscape', badge: '리포트 무료' },
];

const GRAVE_ITEMS = [
  { name: '예초·주변 정리', desc: '전문 장비로 깔끔하게 예초 및 정비', img: 'https://readdy.ai/api/search-image?query=professional%20grass%20cutting%20Korean%20ancestral%20grave%20stone%20monument%20trimmed%20lawn%20peaceful%20green%20hills%20bright&width=360&height=220&seq=h-svc-08&orientation=landscape' },
  { name: '식생·토양 관리', desc: '잔디 건강 유지 및 토양 정밀 관리', img: 'https://readdy.ai/api/search-image?query=soil%20grass%20health%20management%20Korean%20ancestral%20grave%20green%20lawn%20care%20professional%20tools%20bright%20light&width=360&height=220&seq=h-svc-09&orientation=landscape' },
  { name: '석물 시설물 관리', desc: '비석·상석 등 석물 세척 및 보수', img: 'https://readdy.ai/api/search-image?query=professional%20cleaning%20Korean%20stone%20grave%20monument%20washing%20restoration%20expert%20bright%20daylight%20green%20hills&width=360&height=220&seq=h-svc-10&orientation=landscape' },
  { name: '의례 대행·모니터링', desc: '성묘 대행 및 정기 현장 모니터링', img: 'https://readdy.ai/api/search-image?query=respectful%20memorial%20service%20Korean%20ancestral%20grave%20offerings%20incense%20peaceful%20green%20hillside%20bright%20daylight&width=360&height=220&seq=h-svc-11&orientation=landscape' },
];

const ALL_ITEMS = [
  ...HOUSE_ITEMS.map(i => ({ ...i, cat: '주택' })),
  ...GRAVE_ITEMS.map(i => ({ ...i, cat: '산소' })),
];
const DOUBLED = [...ALL_ITEMS, ...ALL_ITEMS];

export default function Hero({ onSimulatorQuote }: HeroProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSimulator, setShowSimulator] = useState(false);
  const [popup, setPopup] = useState<null | 'house' | 'grave'>(null);

  useEffect(() => {
    if (popup) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [popup]);

  const scrollToSection = (id: string) => {
    setMobileMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSimulatorConsult = (data: SimulatorQuoteData) => {
    onSimulatorQuote(data);
    setShowSimulator(false);
    setTimeout(() => {
      const el = document.getElementById('contact');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const popupItems = popup === 'house' ? HOUSE_ITEMS : GRAVE_ITEMS;
  const popupTitle = popup === 'house' ? '🏠 주택 유지보수관리' : '🌿 산소 벌초';
  const popupAccent = popup === 'house' ? 'rgba(45,106,79,0.35)' : 'rgba(201,162,39,0.35)';
  const popupBar = popup === 'house' ? '#2d6a4f' : '#c9a227';

  return (
    <>
      <style>{`
        @keyframes hero-ticker-kf {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        .hero-ticker-run {
          display: flex;
          gap: 16px;
          width: max-content;
          animation: hero-ticker-kf 55s linear infinite;
        }
        .hero-ticker-run:hover { animation-play-state: paused; }

        /* 반응형: 모바일 전용 요소 */
        .hero-mobile-only { display: block; }
        .hero-desktop-only { display: none; }

        /* 768px 이상: PC 레이아웃 */
        @media (min-width: 768px) {
          .hero-mobile-only { display: none !important; }
          .hero-desktop-only { display: block !important; }
          .hero-nav-desktop { display: flex !important; }
          .hero-nav-mobile-btn { display: none !important; }
          .hero-h1 { font-size: 48px !important; }
          .hero-desc { font-size: 18px !important; }
          .hero-img-wrap { height: 440px !important; border-radius: 24px !important; }
          .hero-content-pad { padding-top: 160px !important; padding-bottom: 80px !important; }
          .hero-badge-tag { font-size: 13px !important; }
          .hero-section-h2 { font-size: 36px !important; }
          .hero-section-p { font-size: 16px !important; }
        }

        /* 767px 이하: 모바일 레이아웃 */
        @media (max-width: 767px) {
          .hero-mobile-only { display: block !important; }
          .hero-desktop-only { display: none !important; }
          .hero-nav-desktop { display: none !important; }
          .hero-nav-mobile-btn { display: flex !important; }
          .hero-h1 { font-size: 24px !important; }
          .hero-desc { font-size: 14px !important; }
          .hero-img-wrap { height: 192px !important; border-radius: 16px !important; }
          .hero-content-pad { padding-top: 96px !important; padding-bottom: 48px !important; }
          .hero-section-h2 { font-size: 22px !important; }
          .hero-section-p { font-size: 13px !important; }
        }

        /* 모바일 드롭다운 메뉴 */
        .hero-mobile-menu { display: none; }
        .hero-mobile-menu.open { display: flex; }
      `}</style>

      {/* ── HERO 배경 영역 ── */}
      <div style={{ position: 'relative', background: 'linear-gradient(135deg, #0d1f3c 0%, #152a52 60%, #111e3a 100%)' }}>
        <div style={{ position: 'absolute', inset: 0, opacity: 0.2, backgroundImage: 'radial-gradient(circle at 20% 50%, #b8860b 0%, transparent 50%), radial-gradient(circle at 80% 20%, #c9a227 0%, transparent 40%)', pointerEvents: 'none' }} />

        {/* 네비게이션 */}
        <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50, background: 'rgba(13,31,60,0.92)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(201,162,39,0.2)' }}>
          <div style={{ maxWidth: '1152px', margin: '0 auto', padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <span style={{ fontSize: '24px' }}>🏡</span>
              <span style={{ color: '#fff', fontWeight: 700, fontSize: '16px' }}>홈 &amp; 그레이브</span>
            </div>

            {/* PC 메뉴 */}
            <div className="hero-nav-desktop" style={{ alignItems: 'center', gap: '8px' }}>
              {[['vision','우리의 약속'],['sop','작업과정'],['market','차별점'],['services','서비스·가격']].map(([id, label]) => (
                <button key={id} onClick={() => scrollToSection(id)} style={{ padding: '8px 16px', borderRadius: '6px', color: '#fff', fontWeight: 600, fontSize: '14px', background: 'linear-gradient(180deg,#1e3a6e,#152a52)', border: '1px solid rgba(201,162,39,0.3)', cursor: 'pointer', whiteSpace: 'nowrap' }}>{label}</button>
              ))}
              <a href="https://home-grave.com" target="_blank" rel="noopener noreferrer nofollow" style={{ padding: '8px 16px', borderRadius: '6px', color: '#0d1f3c', fontWeight: 700, fontSize: '14px', background: 'linear-gradient(180deg,#e8c84a,#b8860b)', cursor: 'pointer', whiteSpace: 'nowrap', textDecoration: 'none' }}>공식도메인</a>
            </div>

            {/* 모바일 햄버거 */}
            <button className="hero-nav-mobile-btn" style={{ width: '40px', height: '40px', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', background: 'rgba(201,162,39,0.1)', cursor: 'pointer', border: 'none' }} onClick={() => setMobileMenuOpen(v => !v)}>
              <i className={mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} style={{ color: '#c9a227', fontSize: '22px' }} />
            </button>
          </div>

          {/* 모바일 드롭다운 메뉴 */}
          <div className={`hero-mobile-menu${mobileMenuOpen ? ' open' : ''}`} style={{ background: '#0d1f3c', borderTop: '1px solid rgba(201,162,39,0.2)', padding: '12px 16px', flexDirection: 'column', gap: '8px' }}>
            {[['vision','우리의 약속'],['sop','작업과정'],['market','차별점'],['services','서비스·가격']].map(([id, label]) => (
              <button key={id} onClick={() => scrollToSection(id)} style={{ width: '100%', textAlign: 'left', padding: '12px 16px', borderRadius: '12px', color: '#c9a227', fontWeight: 600, fontSize: '16px', background: 'rgba(255,255,255,0.1)', cursor: 'pointer', border: 'none' }}>{label}</button>
            ))}
            <a href="https://home-grave.com" target="_blank" rel="noopener noreferrer" style={{ display: 'block', textAlign: 'center', padding: '12px 16px', borderRadius: '12px', color: '#0d1f3c', fontWeight: 700, background: '#c9a227', textDecoration: 'none' }}>공식도메인</a>
          </div>
        </nav>

        {/* 히어로 콘텐츠 */}
        <div className="hero-content-pad" style={{ position: 'relative', paddingLeft: '16px', paddingRight: '16px' }}>
          <div style={{ maxWidth: '1152px', margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
            <div style={{ display: 'inline-block', padding: '6px 16px', borderRadius: '999px', background: 'rgba(201,162,39,0.15)', color: '#c9a227', fontWeight: 700, marginBottom: '20px', border: '1px solid rgba(201,162,39,0.4)' }} className="hero-badge-tag">
              ✨ 진주 지역 프리미엄 공간 케어의 기준
            </div>
            <h1 className="hero-h1" style={{ color: '#ffffff', fontWeight: 800, textAlign: 'center', marginBottom: '20px', lineHeight: 1.4 }}>
              <span style={{ display: 'block', letterSpacing: '-0.05em' }}>한 번의 예약으로 누리는 안심,</span>
              <span style={{ display: 'block', letterSpacing: '-0.08em' }}>집과 산소를 위한 가장 지능적인 선택.</span>
            </h1>
            <p className="hero-desc" style={{ color: 'rgba(200,216,240,0.8)', lineHeight: 1.7, maxWidth: '640px', marginBottom: '32px' }}>
              단순 노동을 넘어 첨단 위성 데이터와 드론 기술로 관리합니다.<br />
              소중한 분들의 안식처를 과학적이고 정성스럽게 케어 해 드립니다.
            </p>
          </div>

          {/* 대표 이미지 */}
          <div style={{ maxWidth: '1152px', margin: '0 auto' }}>
            <div className="hero-img-wrap" style={{ position: 'relative', width: '100%', overflow: 'hidden', border: '1px solid rgba(201,162,39,0.2)' }}>
              <img src="https://static.readdy.ai/image/b6088df85b1af9036dd560688f3d7991/51a0d4bed1937e61f4807361b9503c65.jpeg" alt="홈 & 그레이브 공간 케어 현장" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }} />
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(13,31,60,0.7), transparent)' }} />
              <div style={{ position: 'absolute', bottom: '12px', left: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ background: 'rgba(13,31,60,0.85)', color: '#c9a227', fontSize: '12px', fontWeight: 700, padding: '5px 12px', borderRadius: '999px', border: '1px solid rgba(201,162,39,0.4)' }}>✨ 진주 현장 직접 관리</span>
                <span style={{ background: 'rgba(13,31,60,0.85)', color: 'rgba(200,216,240,0.8)', fontSize: '12px', fontWeight: 500, padding: '5px 12px', borderRadius: '999px', border: '1px solid rgba(255,255,255,0.2)' }}>드론 · 위성 데이터 활용</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── 서비스 섹션 ── */}
      <div id="service-ticker" style={{ width: '100%', background: 'linear-gradient(180deg, #111e3a 0%, #0d1f3c 100%)', paddingTop: '52px', paddingBottom: '52px', boxSizing: 'border-box' }}>
        <div style={{ textAlign: 'center', padding: '0 24px 28px' }}>
          <p style={{ color: '#c9a227', fontSize: '11px', fontWeight: 700, letterSpacing: '0.35em', textTransform: 'uppercase', marginBottom: '10px' }}>Our Services</p>
          <h2 className="hero-section-h2" style={{ fontFamily: 'sans-serif', fontWeight: 900, color: '#ffffff', letterSpacing: '-0.04em', lineHeight: 1.2, marginBottom: '10px' }}>
            우리가 하는 일
          </h2>
          <p className="hero-section-p" style={{ color: 'rgba(200,216,240,0.7)', lineHeight: 1.7, maxWidth: '420px', margin: '0 auto' }}>
            전원주택 유지보수부터 산소 벌초까지<br />직접 확인하기 어려운 모든 일을 대신합니다
          </p>
        </div>

        {/* PC: 자동 스크롤 티커 */}
        <div className="hero-desktop-only" style={{ position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '80px', background: 'linear-gradient(to right, #0d1f3c, transparent)', zIndex: 2, pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '80px', background: 'linear-gradient(to left, #111e3a, transparent)', zIndex: 2, pointerEvents: 'none' }} />
          <div className="hero-ticker-run">
            {DOUBLED.map((item, i) => <SvcCard key={i} item={item} />)}
          </div>
        </div>

        {/* 모바일: 버튼 2개 → 팝업 */}
        <div className="hero-mobile-only" style={{ padding: '0 20px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <button
              onClick={() => setPopup('house')}
              style={{
                width: '100%',
                padding: '20px 24px',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, rgba(45,106,79,0.9) 0%, rgba(30,80,55,0.9) 100%)',
                border: '1px solid rgba(45,106,79,0.6)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                textAlign: 'left',
              }}
            >
              <div>
                <div style={{ fontSize: '22px', marginBottom: '6px' }}>🏠</div>
                <div style={{ color: '#ffffff', fontWeight: 900, fontSize: '18px', marginBottom: '4px' }}>주택 유지보수관리</div>
                <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '13px', lineHeight: 1.5 }}>전기·수처리·냉난방·마감재 등 7가지</div>
              </div>
              <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginLeft: '12px' }}>
                <i className="ri-arrow-right-line" style={{ color: '#ffffff', fontSize: '18px' }} />
              </div>
            </button>

            <button
              onClick={() => setPopup('grave')}
              style={{
                width: '100%',
                padding: '20px 24px',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, rgba(92,74,53,0.9) 0%, rgba(70,55,38,0.9) 100%)',
                border: '1px solid rgba(201,162,39,0.5)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                textAlign: 'left',
              }}
            >
              <div>
                <div style={{ fontSize: '22px', marginBottom: '6px' }}>🌿</div>
                <div style={{ color: '#ffffff', fontWeight: 900, fontSize: '18px', marginBottom: '4px' }}>산소 벌초</div>
                <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '13px', lineHeight: 1.5 }}>예초·식생·석물·의례 대행 등 4가지</div>
              </div>
              <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginLeft: '12px' }}>
                <i className="ri-arrow-right-line" style={{ color: '#ffffff', fontSize: '18px' }} />
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* ── 팝업 모달 (모바일) ── */}
      {popup && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setPopup(null); }}
          style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
        >
          <div style={{ width: '100%', maxWidth: '520px', background: '#0d1f3c', borderRadius: '24px 24px 0 0', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid rgba(201,162,39,0.25)', borderBottom: 'none' }}>
            <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div>
                <div style={{ color: '#c9a227', fontSize: '11px', fontWeight: 700, letterSpacing: '0.3em', textTransform: 'uppercase', marginBottom: '4px' }}>Services</div>
                <div style={{ color: '#ffffff', fontWeight: 900, fontSize: '20px', letterSpacing: '-0.03em' }}>{popupTitle}</div>
              </div>
              <button onClick={() => setPopup(null)} style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="ri-close-line" style={{ color: '#ffffff', fontSize: '20px' }} />
              </button>
            </div>
            <div style={{ overflowY: 'auto', padding: '16px 16px 24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {popupItems.map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '14px', background: 'rgba(255,255,255,0.06)', borderRadius: '14px', overflow: 'hidden', border: `1px solid ${popupAccent}` }}>
                  <div style={{ width: '90px', height: '80px', flexShrink: 0, overflow: 'hidden' }}>
                    <img src={item.img} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }} />
                  </div>
                  <div style={{ flex: 1, padding: '12px 14px 12px 0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ color: '#ffffff', fontWeight: 900, fontSize: '15px', letterSpacing: '-0.02em' }}>{item.name}</span>
                      {'badge' in item && item.badge && (
                        <span style={{ background: '#c9a227', color: '#ffffff', fontSize: '10px', fontWeight: 900, padding: '2px 8px', borderRadius: '999px', whiteSpace: 'nowrap' }}>{item.badge as string}</span>
                      )}
                    </div>
                    <p style={{ color: 'rgba(200,216,240,0.65)', fontSize: '12px', lineHeight: 1.5, margin: 0 }}>{item.desc}</p>
                    <div style={{ marginTop: '8px', height: '2px', width: '24px', borderRadius: '999px', background: popupBar }} />
                  </div>
                </div>
              ))}
              <button
                onClick={() => { setPopup(null); scrollToSection('services'); }}
                style={{ width: '100%', padding: '16px', borderRadius: '14px', background: 'linear-gradient(135deg, #c9a227, #b8860b)', color: '#0d1f3c', fontWeight: 900, fontSize: '16px', border: 'none', cursor: 'pointer', marginTop: '4px' }}
              >
                무료 견적 신청하기 →
              </button>
            </div>
          </div>
        </div>
      )}

      {showSimulator && (
        <QuoteSimulator isOpen={showSimulator} onClose={() => setShowSimulator(false)} onConsult={handleSimulatorConsult} />
      )}
    </>
  );
}

interface CardItem { cat: string; name: string; desc: string; img: string; badge?: string; }
function SvcCard({ item }: { item: CardItem }) {
  const isHouse = item.cat === '주택';
  return (
    <div style={{ flexShrink: 0, width: '270px', borderRadius: '14px', overflow: 'hidden', background: 'rgba(255,255,255,0.07)', border: `1px solid ${isHouse ? 'rgba(45,106,79,0.45)' : 'rgba(201,162,39,0.3)'}` }}>
      <div style={{ position: 'relative', height: '156px', overflow: 'hidden' }}>
        <img src={item.img} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }} />
        <div style={{ position: 'absolute', top: '8px', left: '8px', background: isHouse ? 'rgba(45,106,79,0.92)' : 'rgba(92,74,53,0.92)', color: '#ffffff', fontSize: '10px', fontWeight: 700, padding: '2px 9px', borderRadius: '999px' }}>{item.cat}</div>
        {item.badge && <div style={{ position: 'absolute', top: '8px', right: '8px', background: '#c9a227', color: '#ffffff', fontSize: '10px', fontWeight: 900, padding: '2px 9px', borderRadius: '999px' }}>{item.badge}</div>}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.35), transparent)' }} />
      </div>
      <div style={{ padding: '11px 13px 14px' }}>
        <h3 style={{ fontFamily: 'sans-serif', fontSize: '15px', fontWeight: 900, color: '#ffffff', letterSpacing: '-0.02em', marginBottom: '4px', lineHeight: 1.3 }}>{item.name}</h3>
        <p style={{ fontSize: '12px', color: 'rgba(200,216,240,0.65)', lineHeight: 1.5 }}>{item.desc}</p>
        <div style={{ marginTop: '9px', height: '2px', width: '26px', borderRadius: '999px', background: isHouse ? '#2d6a4f' : '#c9a227' }} />
      </div>
    </div>
  );
}
